/* THE COACH'S OWN EXERCISE, AS THE WEB EDITS IT — W1.
 *
 * 🔴 **WILLIAM HAS NO iPHONE, so the web is where he builds his library.** The phone's
 * `CoachExerciseFormView` has edited these fields since August; this is the same row, edited from
 * the other side. Nothing here is a second opinion about what an exercise is.
 *
 * ⚠️ **EVERY CONSTANT BELOW WAS READ FROM THE LIVE `pg_constraint`, NOT FROM A DESIGN DOC**
 * (MEASURED 2026-09-18). A vocabulary typed into a second system from memory is this repo's
 * defining bug class, and a CHECK is the one place the database will actually refuse the row —
 * so the refusal is worth reproducing HERE, where the coach can be told which field is wrong,
 * rather than surfacing as a 400 with a constraint name in it.
 *
 *   coach_exercises_movement_pattern_check  11 values — and the column IS NULLABLE, see below
 *   coach_exercises_category_check          5 values, nullable
 *   coach_exercises_difficulty_check        1–10, nullable
 *   coach_exercises_muscles_max             array_length <= 3
 *   coach_exercises_fields_max              array_length <= 3
 *
 * ⚠️ **THE ELEVEN ARE `movement_pattern`'s, NOT the engine's nine `movement_pattern_id` values.**
 * The legacy column mixes patterns with CATEGORIES (`mobility`, `isometric` are not patterns in the
 * engine's sense) and `CLAUDE.md` records that as a live trap; the coach's row uses the eleven and
 * the engine never reads this table. Do not "tidy" this list towards the engine's.
 */
import { PROGRESS_VIEWS, offersOneRMSwitch, type ProgressViewKey } from "./progressViews";
import { storedViews } from "./progressViewPicks";
import { isPlayableHere } from "./videoThumb";
import { addingTrackingField, canAddTrackingField, holdsBothCounts } from "./trackingFieldRule";

/** The eleven, in the CHECK's own order. */
export const MOVEMENT_PATTERNS = [
  "push", "pull", "hinge", "squat", "lunge", "carry",
  "core", "skill", "cardio", "mobility", "isometric",
] as const;
export type MovementPattern = (typeof MOVEMENT_PATTERNS)[number];

/** The five, in the CHECK's own order. Nullable: "not categorised" is a real answer. */
export const CATEGORIES = [
  "strength", "bodyweight", "timed", "distance_short", "distance_long",
] as const;
export type Category = (typeof CATEGORIES)[number];

/**
 * W24(d) — **THEIR WORDS FOR OUR FIVE**, read verbatim off their filter panel
 * (`EVERFIT-MAP` §18-quater depth 3: `Strength · Bodyweight · Timed · Distance (Long) ·
 * Distance (Short)`).
 *
 * 🔴 **THIS IS THE ONE CHIP GROUP WHERE THEIR VOCABULARY AND OURS MATCH ROW FOR ROW**, and it was
 * a surprise: `coach_exercises_category_check` already holds exactly those five values, and the
 * library's filter did not offer the column at all. So the copy here is a LABEL and an ORDER, not
 * a concept — `c.replace("_", " ")` was printing `distance short`, which is the database's
 * spelling on a coach's screen.
 *
 * ⚠️ **THE ORDER IS THEIRS AND THE VALUES ARE OURS.** Their panel puts Long before Short; the
 * CHECK's own order is the other way round. Neither is more correct, and during the copy phase
 * the judge is their screen.
 */
export const CATEGORY_ORDER: readonly Category[] = [
  "strength", "bodyweight", "timed", "distance_long", "distance_short",
] as const;

export const CATEGORY_LABEL: Record<Category, string> = {
  strength: "Strength",
  bodyweight: "Bodyweight",
  timed: "Timed",
  distance_long: "Distance (Long)",
  distance_short: "Distance (Short)",
};

/**
 * WHAT CHOOSING A CATEGORY FILLS IN, AND THE SUBTITLE THAT SAYS SO — copied from their category
 * menu (read 2026-09-23 on his account: `Strength · Weight x Reps`, `Bodyweight · Reps`,
 * `Timed · Time`, `Distance (Long) · Distance (long) x Time`, `Distance (Short) · Distance (short)
 * x Time`). Picking one REPLACES the tracking fields with its list, exactly as theirs does; the
 * coach may then add, remove and reorder. ⚠️ ONLY a pick does this — loading a stored row never
 * rewrites its fields, because a row's order is the set table's column order.
 */
export const CATEGORY_TRACKING: Readonly<Record<Category, readonly TrackingField[]>> = {
  strength: ["weight", "reps"],
  bodyweight: ["reps"],
  timed: ["time"],
  distance_long: ["distance_km", "time"],
  distance_short: ["distance_m", "time"],
};

export const CATEGORY_SUBTITLE: Readonly<Record<Category, string>> = {
  strength: "Weight x Reps",
  bodyweight: "Reps",
  timed: "Time",
  distance_long: "Distance (long) x Time",
  distance_short: "Distance (short) x Time",
};

/** What a set of this exercise records. The phone's `TrackingField` raw values, exactly. */
export const TRACKING_FIELDS = [
  "reps", "weight", "time", "rounds", "distance_m", "distance_km",
] as const;
export type TrackingField = (typeof TRACKING_FIELDS)[number];

export const TRACKING_FIELD_LABEL: Record<TrackingField, string> = {
  reps: "Reps",
  weight: "Weight",
  time: "Time",
  rounds: "Rounds",
  distance_m: "Distance (m)",
  distance_km: "Distance (km)",
};

/** Both array CHECKs are `<= 3`. One constant, because they are the same rule twice. */
export const MAX_PRIMARY_MUSCLES = 3;
export const MAX_TRACKING_FIELDS = 3;
export const MIN_DIFFICULTY = 1;
export const MAX_DIFFICULTY = 10;

/* ── the video bucket ──────────────────────────────────────────────────────────────────────────
 *
 * MEASURED from `storage.buckets` 2026-09-18: `coach-videos` is PUBLIC, `file_size_limit`
 * 209,715,200 (200 MiB exactly), mime types video/mp4, video/quicktime, image/jpeg.
 */
export const VIDEO_BUCKET = "coach-videos";

/* 🔴 **WHERE A DEMO VIDEO CAME FROM — `demo_video_source`, and writing it is not optional.**
 *
 * `tools/demo-videos/verify.sh` is the PRE-BUILD gate and it COUNTS this column: a NULL reads as a
 * clip whose origin nobody recorded, which is exactly what the column was added to stop. The web
 * upload wrote the url and not the provenance until 2026-09-18.
 *
 * ⚠️ **MEASURED LIVE rather than chosen, because the vocabulary already exists and a sixth
 * spelling would be a vocabulary mismatch of the kind this repo is named for.** On
 * `coach_exercises` 2026-09-18: `starter:seed` 84 · `youtube:title-reviewed` 50 · **null 36** ·
 * `owner:upload` 7 · `engine:name-match` 3 · `owner:filmed` 3 ·
 * `youtube:title-reviewed-20260913` 1.
 *
 * ⚠️ **AND IT IS `owner:upload`, NOT `owner:filmed`, WHICH WOULD BREAK THE GATE.** `verify.sh`'s
 * claim 3 asserts that **every `owner:filmed` row has its MASTER on this Mac** at
 * `media/demo-masters/<slug>.<ext>` — a coach uploading from a browser in another country has no
 * master here, so stamping `owner:filmed` would fail the build for a correct upload.
 *
 * ⚠️ **THE 36 NULLS ARE THE PHONE'S**: `demo_video_source` appears NOWHERE in the Swift source
 * (measured), so the app's own saves have never stamped it. That is a separate gap, recorded here
 * rather than fixed from the web.
 */
export const VIDEO_SOURCE_COACH_UPLOAD = "owner:upload";

/** A link the coach PASTED — their own choice of somebody else's video.
 *
 * ⚠️ **A NEW MEMBER OF AN EXISTING FAMILY, NOT A NEW VOCABULARY.** `owner:filmed` and
 * `owner:upload` are both live; this is the third thing an owner can do, and it is kept apart from
 * `youtube:title-reviewed` (50 rows) because that one means *a review pass found this and a human
 * checked the title* — a different claim entirely, and one nobody should inherit by pasting a URL.
 * ⚠️ It is added to the COLUMN'S OWN COMMENT in the same change, so the gate's vocabulary stays
 * one list rather than becoming two.
 */
export const VIDEO_SOURCE_COACH_LINK = "owner:link";
export const VIDEO_SIZE_LIMIT_BYTES = 209_715_200;
export const VIDEO_MIME_TYPES = ["video/mp4", "video/quicktime", "image/jpeg"] as const;

/* 🔴 **THE OWNER FOLDER IS LOWERCASE, AND IT IS NOT A STYLE CHOICE.**
 *
 * Every owner-scoped bucket policy compares `(storage.foldername(name))[1] = auth.uid()::text`, and
 * Postgres renders a uuid LOWERCASE. `CLAUDE.md` records this as its own named trap: FIVE builders
 * in the app, ONE of them right, and the four that copied the shape without the `.lowercased()`
 * were refused with 42501 — *"new row violates row-level security policy"* — on the owner's own
 * upload, under comments that correctly quoted the policy they then failed.
 *
 * ⚠️ So this is the WEB's twin of `StoragePath`, and it lowercases at the ONE place a path is made.
 * A capital letter here is invisible to the compiler, to every fixture and to a policy review.
 */
export function videoObjectPath(coachId: string, stem: string): string {
  return `${coachId.toLowerCase()}/${stem}`;
}

/** A stem that never collides with a previous upload's.
 *
 * ⚠️ **A RE-UPLOAD TAKES A NEW STEM, NEVER THE SAME PATH.** The bucket is public and the CDN caches
 * by URL: `CLAUDE.md` records a DELETED object still being served under `max-age`, so replacing a
 * file at the same path leaves coaches and clients watching the old clip with nothing to say why.
 */
export function videoStem(now: Date, random: string, extension: string): string {
  const stamp = now.toISOString().replace(/[-:]/g, "").replace(/\..+$/, "");
  return `${stamp}-${random}.${extension}`;
}

/* ── what the coach typed, and whether the database will take it ───────────────────────────────*/

export type ExerciseDraft = {
  name: string;
  movementPattern: MovementPattern | null;
  equipment: string[];
  primaryMuscles: string[];
  trackingFields: TrackingField[];
  /** `coach_exercises.progress_views` — EMPTY = every view the logged fields offer (`progressViewPicks`). */
  progressViews: ProgressViewKey[];
  /** W125 — `coach_exercises.one_rm_enabled`: the `1RM` switch, OFF by default; drawn only on weight + reps. */
  oneRMEnabled: boolean;
  /** W125 — `coach_exercises.body_is_load`: the estimate counts the client's bodyweight. Meaningful only with the switch on. */
  bodyIsLoad: boolean;
  category: Category | null;
  difficulty: number | null;
  instructions: string[];
  tips: string[];
  notes: string;
  demoVideoUrl: string | null;
};

/** One problem, named by the FIELD it is about.
 *
 * ⚠️ **A REFUSAL NAMES SOMETHING THE COACH CAN CHANGE.** The codex's own lesson from his wife's
 * error screen: an error that cannot be acted on is a dead button with words on it. Every message
 * here says which field and what to do, never a constraint name.
 */
export type DraftProblem = { field: keyof ExerciseDraft; message: string };

export function validateDraft(draft: ExerciseDraft): DraftProblem[] {
  const problems: DraftProblem[] = [];

  if (draft.name.trim() === "") {
    problems.push({ field: "name", message: "Give the exercise a name." });
  }
  // ⚠️ **THIS ONE IS OURS, NOT THE DATABASE'S — and I had it backwards until I executed it.**
  // The CHECK reads `movement_pattern in (…)` with no `is null or`, which LOOKS like a NOT NULL
  // and is not: a CHECK evaluating to NULL PASSES, so Postgres accepts a null pattern (MEASURED
  // 2026-09-18, twice — `information_schema` says `is_nullable = YES`, and a temp table with the
  // same CHECK accepted a null row). So requiring it here is a PRODUCT decision: an exercise with
  // no pattern cannot be placed by anything that groups a session by movement, and a coach who
  // skips it will not find out for weeks. Stated as ours so nobody later "fixes" it against the
  // schema and finds the schema agreeing with them.
  if (draft.movementPattern === null) {
    problems.push({ field: "movementPattern", message: "Choose a movement pattern." });
  }
  if (draft.primaryMuscles.length > MAX_PRIMARY_MUSCLES) {
    problems.push({
      field: "primaryMuscles",
      message: `Choose at most ${MAX_PRIMARY_MUSCLES} primary muscles — the main ones this trains.`,
    });
  }
  if (draft.trackingFields.length > MAX_TRACKING_FIELDS) {
    problems.push({
      field: "trackingFields",
      message: `Track at most ${MAX_TRACKING_FIELDS} things per set.`,
    });
  }
  if (draft.trackingFields.length === 0) {
    problems.push({
      field: "trackingFields",
      message: "Choose what a set of this records — reps, weight, time…",
    });
  }
  if (
    draft.difficulty !== null &&
    (draft.difficulty < MIN_DIFFICULTY || draft.difficulty > MAX_DIFFICULTY ||
      !Number.isInteger(draft.difficulty))
  ) {
    problems.push({
      field: "difficulty",
      message: `Difficulty is ${MIN_DIFFICULTY}–${MAX_DIFFICULTY}, or leave it unrated.`,
    });
  }
  return problems;
}

/** Why an upload cannot start. `null` means it can.
 *
 * ⚠️ **CHECKED HERE AS WELL AS BY THE BUCKET, on purpose.** The bucket refuses an oversized object
 * with a 413 after the whole file has gone up the wire — on a 200 MiB limit and a coach's home
 * connection that is minutes of waiting for a number. Refusing it before the first byte is the
 * difference between a sentence and a mystery.
 */
export function videoRefusal(file: { size: number; type: string }): string | null {
  if (!(VIDEO_MIME_TYPES as readonly string[]).includes(file.type)) {
    return "That file type isn't supported — use an MP4 or a MOV.";
  }
  if (file.size > VIDEO_SIZE_LIMIT_BYTES) {
    // ⚠️ **CEIL, NOT ROUND, and a test is why.** A file ONE BYTE over the limit rounds to 200 and
    // the sentence became *"That video is 200 MB. The limit is 200 MB"* — a refusal that reads as
    // a bug in the app rather than a fact about the file. Rounding up is also the honest direction
    // for "too big": it can never claim a file is smaller than it is.
    const mb = Math.ceil(file.size / 1_048_576);
    return `That video is ${mb} MB. The limit is 200 MB — trim it or export it smaller.`;
  }
  return null;
}

/* ── the row on the wire, and the draft on the screen ──────────────────────────────────────────
 *
 * ⚠️ **THE CONVERSION LIVES HERE, WITH TESTS, AND NOT IN THE SCREEN.** A `.map()` in a component
 * is the one fallible thing in this app with no runner — `Workouts.tsx` records session 2 paying
 * for exactly that, and the rule it left is that a shape conversion is where a silent-blank defect
 * lives. Both directions are pure functions of their input.
 */

/** The columns this editor reads and writes. Narrower than the table on purpose. */
export type ExerciseRow = {
  id: string;
  name: string;
  movement_pattern: string | null;
  /** MEASURED 2026-09-18: every array column is NOT NULL with a `'{}'` default, so a stored row
   *  never carries a null here. They are typed nullable anyway because `draftFromRow` also reads
   *  rows this app did not write, and an absent array must read as empty rather than throw. */
  equipment: string[] | null;
  primary_muscles: string[] | null;
  tracking_fields: string[] | null;
  progress_views?: string[] | null;
  one_rm_enabled?: boolean | null;
  body_is_load?: boolean | null;
  category: string | null;
  difficulty: number | null;
  instructions: string[] | null;
  tips: string[] | null;
  notes: string | null;
  demo_video_url: string | null;
  /** Maintained by the `coach_exercises_set_updated_at` trigger (migration 20260925130000) — the builder's "Saved · …". */
  updated_at?: string | null;
};

/** What is actually WRITTEN — the arrays non-null, because the columns are. */
export type ExerciseWrite = {
  name: string;
  movement_pattern: string | null;
  equipment: string[];
  primary_muscles: string[];
  tracking_fields: string[];
  progress_views: string[];
  one_rm_enabled: boolean;
  body_is_load: boolean;
  category: string | null;
  difficulty: number | null;
  instructions: string[];
  tips: string[];
  notes: string | null;
  demo_video_url: string | null;
};

export const EMPTY_DRAFT: ExerciseDraft = {
  name: "",
  movementPattern: null,
  equipment: [],
  primaryMuscles: [],
  trackingFields: ["reps"],
  progressViews: [],
  oneRMEnabled: false,
  bodyIsLoad: false,
  category: null,
  difficulty: null,
  instructions: [],
  tips: [],
  notes: "",
  demoVideoUrl: null,
};

/** A stored row as the form holds it.
 *
 * ⚠️ **AN UNRECOGNISED VALUE BECOMES `null`, NEVER ITSELF.** The eleven patterns and five
 * categories are CHECKs, so a twelfth cannot be in the table today — but a row written before a
 * constraint existed can be, and a `<select>` whose value matches no option silently shows the
 * FIRST one. That is a save that quietly rewrites a coach's data, which is worse than a field the
 * coach is asked to fill in. Same for a tracking field the phone no longer knows.
 */
export function draftFromRow(row: ExerciseRow): ExerciseDraft {
  const pattern = row.movement_pattern;
  const category = row.category;
  return {
    name: row.name,
    movementPattern: (MOVEMENT_PATTERNS as readonly string[]).includes(pattern ?? "")
      ? (pattern as MovementPattern) : null,
    equipment: row.equipment ?? [],
    primaryMuscles: row.primary_muscles ?? [],
    trackingFields: (row.tracking_fields ?? [])
      .filter((f): f is TrackingField => (TRACKING_FIELDS as readonly string[]).includes(f)),
    // An unknown word is dropped here exactly as the phone ignores it (`ProgressChartPlan.allowed`).
    progressViews: (row.progress_views ?? [])
      .filter((v): v is ProgressViewKey => PROGRESS_VIEWS.some((p) => p.key === v)),
    oneRMEnabled: row.one_rm_enabled === true,
    bodyIsLoad: row.body_is_load === true,
    category: (CATEGORIES as readonly string[]).includes(category ?? "")
      ? (category as Category) : null,
    difficulty: row.difficulty,
    instructions: row.instructions ?? [],
    tips: row.tips ?? [],
    notes: row.notes ?? "",
    demoVideoUrl: row.demo_video_url,
  };
}

/** What gets written back.
 *
 * ⚠️ **IT NAMES EVERY COLUMN IT OWNS AND NOT ONE MORE.** A write struct says which columns this
 * screen is AUTHORITATIVE about — the repo's read/write split — so a column this editor does not
 * show (`is_hold`, the typical ranges, `progression_config`, `thumbnail_url`) is never sent and
 * therefore never blanked by a coach who simply opened a form. An update that spreads the whole
 * row is how an editor quietly erases what another surface wrote.
 *
 * ⚠️ Empty strings become NULL where the column is nullable: `""` and "not set" are different
 * facts, and a notes field a coach cleared should read as absent rather than as a blank string
 * nothing can distinguish from one space.
 */
export function rowFromDraft(draft: ExerciseDraft): ExerciseWrite {
  const trimmedNotes = draft.notes.trim();
  return {
    name: draft.name.trim(),
    movement_pattern: draft.movementPattern,
    equipment: draft.equipment,
    primary_muscles: draft.primaryMuscles,
    tracking_fields: draft.trackingFields,
    // STORED AGAINST THE FIELDS BEING SAVED: a tick list equal to what they offer is written EMPTY.
    progress_views: storedViews(draft.progressViews, draft.trackingFields),
    // W125 — the switch is written OFF unless the exercise logs weight AND reps (a switch the editor no longer draws cannot
    // stay on), and body-is-load only rides an ON switch.
    one_rm_enabled: oneRMStored(draft),
    body_is_load: oneRMStored(draft) && draft.bodyIsLoad,
    category: draft.category,
    difficulty: draft.difficulty,
    instructions: draft.instructions.map((l) => l.trim()).filter((l) => l !== ""),
    tips: draft.tips.map((l) => l.trim()).filter((l) => l !== ""),
    notes: trimmedNotes === "" ? null : trimmedNotes,
    demo_video_url: draft.demoVideoUrl,
  };
}

/** W125 — the `1RM` switch as stored: on only when the coach turned it on AND the exercise logs weight + reps. */
export function oneRMStored(draft: ExerciseDraft): boolean {
  return draft.oneRMEnabled && offersOneRMSwitch(draft.trackingFields);
}

/** Has the coach changed anything? Drives the unsaved-changes guard on close.
 *
 * ⚠️ Compared through `rowFromDraft`, so trailing whitespace and a blank line the coach typed and
 * deleted do not count as a change — otherwise the guard cries wolf and gets clicked through,
 * which is the failure mode that makes a guard worth nothing.
 */
export function draftIsDirty(a: ExerciseDraft, b: ExerciseDraft): boolean {
  return JSON.stringify(rowFromDraft(a)) !== JSON.stringify(rowFromDraft(b));
}

/* 🔴 THE EXERCISE EDITOR'S ✕ (his word, 2026-09-28: *"we need an X button … of the workout builder window and also the
 * exercise builder where it's gonna just close without warning if it's empty of exercises and title and all, but a
 * warning small window blurring and darkening its background. So we choose if we want to close and discard or
 * continue"*). The workout builder's W85 rule, asked of an exercise: the stake `closeAttempt` reads. The editor SAVES AS
 * YOU TYPE (W72 step 3), so, as in the builder, "discard" is a RESTORE of the draft it opened on — or, for an exercise
 * made in this window, its removal — never a withheld write. `exercises` carries "is anything in it": a BLANK form (the
 * empty draft, nothing typed or picked) closes without a word, as an empty workout does. */
export function exerciseCloseStake(draft: ExerciseDraft, opened: ExerciseDraft, createdHere: boolean): {
  readonly exercises: number; readonly changedSinceOpen: boolean; readonly createdHere: boolean;
} {
  return { exercises: draftIsDirty(draft, EMPTY_DRAFT) ? 1 : 0, changedSinceOpen: draftIsDirty(draft, opened), createdHere };
}

/* ── INSTRUCTIONS AS NUMBERED STEPS (his word, 2026-09-23: *"why not do the same we have in our
 * app"*). The phone's `CoachExerciseFormView` is the model, and its reason binds here too: the
 * NUMBER IS A LABEL drawn beside each step, never characters typed into it, so what is SAVED is
 * clean prose (the client's workout screen numbers the steps itself) and renumbering is arithmetic.
 * Each rule below answers where the caret goes next, so the component only has to put it there. */

/** Where the caret lands after an edit: which step, and at what offset in it. */
export type StepCaret = { index: number; offset: number };

/** Enter at `offset` in step `i`: the text after the caret becomes the next step, caret at its start. */
export function splitStep(steps: readonly string[], i: number, offset: number): { steps: string[]; caret: StepCaret } {
  const text = steps[i] ?? "";
  const out = [...steps];
  out.splice(i, 1, text.slice(0, offset), text.slice(offset));
  return { steps: out, caret: { index: i + 1, offset: 0 } };
}

/** Backspace with the caret at the very START of step `i` — the moment his rule is about: the
 *  number, the dot and the space go as ONE character. An EMPTY step is removed and the caret goes to
 *  the end of the step above; a step with words is joined onto the one above, the caret at the join.
 *  The first step has nothing above it: emptied, the list is empty again (`caret` null); with
 *  words, nothing happens. */
export function backspaceStepStart(steps: readonly string[], i: number): { steps: string[]; caret: StepCaret | null } {
  const text = steps[i] ?? "";
  if (i === 0) {
    if (text === "" && steps.length === 1) return { steps: [], caret: null };
    if (text === "") return { steps: steps.slice(1), caret: { index: 0, offset: 0 } };
    return { steps: [...steps], caret: { index: 0, offset: 0 } };
  }
  const above = steps[i - 1] ?? "";
  const out = [...steps];
  out.splice(i - 1, 2, above + text);
  return { steps: out, caret: { index: i - 1, offset: above.length } };
}

/** A pasted block becomes steps — one per non-empty line, with a number a coach pasted along with
 *  it (`1.`, `2)`) taken off, because the number is the list's to draw. Inserted at the caret. */
export function pasteSteps(steps: readonly string[], i: number, from: number, to: number,
                           pasted: string): { steps: string[]; caret: StepCaret } {
  // The line breaks are STRUCTURE before they are blanks: a paste that opens on one starts a new
  // step (found by driving it — dropping blank lines first glued the block onto the step above).
  const parts = pasted.split(/\r?\n/).map((l) => l.replace(/^\s*\d+\s*[.)]\s+/, "").trim());
  const text = steps[i] ?? "";
  const before = text.slice(0, from), after = text.slice(to);
  if (parts.length === 1) {
    // One line is ordinary text: pasted exactly as it is, spaces and all.
    const out = [...steps];
    out[i] = before + pasted + after;
    return { steps: out, caret: { index: i, offset: before.length + pasted.length } };
  }
  const first = before + parts[0]!;
  const last = parts[parts.length - 1]!;
  const middle = parts.slice(1, -1).filter((l) => l !== "");
  // Blank steps are dropped — except the one the caret lands in, which is where the coach types next.
  const block = [first, ...middle].filter((l) => l !== "");
  block.push(last + after);
  const out = [...steps];
  out.splice(i, 1, ...block);
  return { steps: out, caret: { index: i + block.length - 1, offset: last.length } };
}

/** One line per entry, as a textarea holds them. */
export function linesToText(lines: string[]): string { return lines.join("\n"); }
export function textToLines(text: string): string[] {
  return text.split("\n").map((l) => l.trim()).filter((l) => l !== "");
}

/* 🔴 **A STORED `demo_video_url` IS NOT ALWAYS A VIDEO FILE, AND A `<video>` TAG CANNOT PLAY A WEB
 * PAGE.** Found by RENDERING the editor on a real row, 2026-09-18: his `Step-Up` carries
 * `https://www.youtube.com/watch?v=…`, which a `<video src>` shows as a dead player with a
 * crossed-out icon — the coach would read that as their video having been lost.
 *
 * ⚠️ Both kinds are LEGITIMATE and neither is going away: an upload lands in `coach-videos` as an
 * mp4/mov, and a pasted link is the other half of the same field (the live catalogue is largely
 * YouTube today). So the screen asks which it has instead of assuming.
 *
 * ⚠️ **THE TEST IS "CAN A `<video>` ELEMENT PLAY THIS", not "is it YouTube".** A domain list would
 * be a vocabulary to maintain and would call Vimeo, a Drive link and anything else a file. Asking
 * for a media EXTENSION is the question the element actually answers, and everything else — every
 * page, every share link, anything unrecognised — is offered as a LINK, which always works.
 */
export type VideoKind = "file" | "link";

export function videoKind(
  url: string,
  /* 🔴 R17 — THE ORIGIN DECIDES TOO, NOT ONLY THE EXTENSION. This function had the same
     defect the thumbnail did: an `.mp4` on any host came back `"file"` and went into a
     `<video src>`, while the page's `media-src` allows only `'self'`, `blob:` and the Supabase
     origin — so the editor drew a player that never starts, which is precisely the reading this
     function's own comment calls worse than a link.
     ⚠️ It is the SAME rule as the tile's, asked of the same function, because the editor and the
     tile disagreeing about one video is the vocabulary split this repo keeps paying for. */
  origin: string = typeof window === "undefined" ? "" : window.location.origin,
): VideoKind {
  let path: string;
  try {
    path = new URL(url, origin || undefined).pathname.toLowerCase();
  } catch {
    // Not a URL this browser can parse. A link is the safe answer: it may still be openable, and
    // a player that cannot play is worse than a link that might not open.
    return "link";
  }
  if (!/\.(mp4|mov|m4v|webm)$/.test(path)) return "link";
  return isPlayableHere(url, origin) ? "file" : "link";
}

/* 🔴 `toggleTrackingField` IS DELETED, AND ITS REASON IS RE-HOMED RATHER THAN LOST (W24(f),
 * 2026-09-19). It normalised the ticked set to the vocabulary's own order, because the editor's
 * checkbox row APPENDED on tick — so unticking and reticking `reps` moved it to the END and
 * silently reordered the set-table columns of every workout built from that exercise, *"with
 * nothing on the form saying so"*.
 *
 * ⚠️ **THE LESSON SURVIVES IN THE RULES BELOW AND ON THE FORM.** The order is the SET TABLE's
 * column order (`builder.columnsFor`); it is now a NUMBERED list with arrows, and the legend says
 * `in this order`. A silent reorder was the defect — a visible, chosen one is the feature. Its
 * last caller went with the checkbox row, and a function with no caller is the dead-branch shape
 * this repo's codex names.
 */

/* ── W24(f) — `TRACKING FIELDS` AS AN ORDERED, ADDABLE LIST ──────────────────────────────────
 *
 * `EVERFIT-MAP` §18-quater depth 3: theirs is a NUMBERED list on the exercise (`1. Reps`) with an
 * `⊕ Add new` link. W24(f) calls it *"the one that touches a live defect"* — `tracking_fields` is
 * the column R19 measured, where the coach-library SEEDER has never carried it, so a seeded coach
 * gets a library that cannot be logged.
 *
 * 🔴 **THE PREMISE IS CORRECTED RATHER THAN QUOTED: THE WEB EDITOR HAS HAD A CONTROL FOR THIS
 * COLUMN SINCE W1** — a row of checkboxes, `What a set records`. W24(f) says *"we have no editor
 * for it anywhere"*, and that is measurably false of this screen. What was missing is the ORDER.
 *
 * 🔴 **AND THE ORDER IS LOAD-BEARING: IT IS THE SET TABLE'S COLUMN ORDER.** `builder.columnsFor`
 * hands this array straight to the table a coach fills in. The checkbox row deliberately
 * NORMALISED to the vocabulary's own order, for a stated reason — appending on tick meant
 * unticking and reticking moved a field to the end, *"with nothing on the form saying so"*. A
 * NUMBERED LIST WITH ARROWS IS THAT REASON ANSWERED, not overruled: the order becomes something
 * the coach can see and move on purpose, which is the whole difference between a silent reorder
 * and a deliberate one.
 *
 * ⚠️ EVERY RULE BELOW RETURNS THE LIST UNCHANGED when it is asked for something impossible — a
 * field already there, a field that is not, a move off either end. A silent no-op is the right
 * answer for a control that should not have been offered; what must never happen is a list that
 * grows past the CHECK's `array_length <= 3` or holds a value the column does not allow.
 */
export function addTrackingField(current: readonly string[], field: string): TrackingField[] {
  if (!(TRACKING_FIELDS as readonly string[]).includes(field)) return [...current] as TrackingField[];
  // W136 — the Swift rule's own answer (`LoadFieldPolicy.adding`, GENERATED): Time REPLACES Reps in place (and the reverse),
  // every other add appends, nothing passes the cap.
  return addingTrackingField(current, field) as TrackingField[];
}

export function removeTrackingField(current: readonly string[], field: string): TrackingField[] {
  return current.filter((f) => f !== field) as TrackingField[];
}

/** Move the field at `from` by `by` places. Off either end is a no-op. */
export function moveTrackingField(current: readonly string[], from: number, by: number): TrackingField[] {
  const to = from + by;
  if (from < 0 || from >= current.length || to < 0 || to >= current.length) {
    return [...current] as TrackingField[];
  }
  const out = [...current];
  const [moved] = out.splice(from, 1);
  out.splice(to, 0, moved!);
  return out as TrackingField[];
}

/** Where a dragged card lands: the GAP it is dropped into (0 = before the first card, `n` = after
 *  the last), read off the cards' own boxes, so a drop in the space BETWEEN two cards is a drop and
 *  not a cancel. 🔴 His report, 2026-09-23: *"I try drag and it just disappears and then when release
 *  it appears again after a sec"* — the drop was accepted only ON another card, so releasing in a gap
 *  made the browser animate the card home. The cards wrap, so a row is found first (a pointer above
 *  a card's bottom edge is in that card's row or an earlier one), then the side of its midline. */
export function dropGap(boxes: readonly { left: number; top: number; width: number; height: number }[],
                        x: number, y: number): number {
  for (let i = 0; i < boxes.length; i++) {
    const b = boxes[i]!;
    if (y < b.top) return i;
    if (y <= b.top + b.height && x < b.left + b.width / 2) return i;
  }
  return boxes.length;
}

/** The card at `from` dropped into gap `gap`. A gap either side of the card itself moves nothing. */
export function dropTrackingField(current: readonly string[], from: number, gap: number): TrackingField[] {
  const to = gap > from ? gap - 1 : gap;
  return moveTrackingField(current, from, to - from);
}

/** W136 — A FIELD-LIST EDIT THAT STILL HOLDS BOTH REPS AND TIME IS REFUSED (Fable's ruling, 2026-10-02). A stored legacy pair
 *  opens as it is, both chips and the one line under them, and is never rewritten for the coach: a move or an add that keeps
 *  both returns the list unchanged; removing one of the two is the only edit that lands. */
export function acceptTrackingFields(current: readonly string[], next: readonly string[]): TrackingField[] {
  return (holdsBothCounts(next) ? [...current] : [...next]) as TrackingField[];
}

/** The fields a coach may still add — every word the one rule would change the list with (`canAddTrackingField`): what is
 *  not there yet while there is room, and, W136, `+ Time` beside Reps (or `+ Reps` beside Time) even at the cap, because
 *  it swaps rather than grows. */
export function addableTrackingFields(current: readonly string[]): TrackingField[] {
  return TRACKING_FIELDS.filter((f) => canAddTrackingField(current, f));
}
